import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, LifeBuoy, RotateCcw, X } from "lucide-react";
import { HelpMessage } from "@/components/help/HelpMessage";
import { HelpComposer } from "@/components/help/HelpComposer";
import { cn } from "@/lib/utils";

const iconBtn = "grid h-9 w-9 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground";

function HelpHeader({ canReset, onReset, onClose }) {
  return (
    <div className="flex items-center gap-3 border-b px-4 py-3">
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary text-primary-foreground"><LifeBuoy className="h-5 w-5" /></span>
      <div className="min-w-0 flex-1">
        <p className="font-display font-semibold leading-tight">Assistente de ajuda</p>
        <p className="text-xs text-muted-foreground">Dúvidas sobre o uso do sistema</p>
      </div>
      {canReset && <button type="button" onClick={onReset} title="Nova conversa" aria-label="Nova conversa" className={iconBtn} data-testid="help-reset-btn"><RotateCcw className="h-4 w-4" /></button>}
      <button type="button" onClick={onClose} title="Fechar" aria-label="Fechar" className={iconBtn} data-testid="help-close-btn"><X className="h-5 w-5" /></button>
    </div>
  );
}

function HelpWelcome({ firstName, starters, onAsk }) {
  return (
    <div className="space-y-5" data-testid="help-welcome">
      <div className="rounded-2xl bg-primary/10 p-4">
        <p className="font-display text-lg font-semibold">Olá, {firstName}!</p>
        <p className="mt-1 text-sm text-muted-foreground">Pergunte como fazer qualquer coisa no sistema. Eu explico o passo a passo e levo você até a tela certa.</p>
      </div>
      {starters.length > 0 && (
        <div>
          <p className="eyebrow mb-2">Sugestões para esta tela</p>
          <div className="flex flex-col gap-2">
            {starters.map((s, i) => (
              <motion.button key={s.id} type="button" onClick={() => onAsk(s.title, s.id)} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}
                className="group flex items-center justify-between gap-2 rounded-xl border bg-card px-3 py-2.5 text-left text-sm transition-colors hover:border-primary/50 hover:bg-primary/5" data-testid={`help-starter-${s.id}`}>
                {s.title}<ArrowUpRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-primary" />
              </motion.button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Typing() {
  return (
    <div className="flex w-fit gap-1 rounded-2xl rounded-bl-md bg-muted px-4 py-3" data-testid="help-typing">
      {[0, 150, 300].map((d) => <span key={d} className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/60" style={{ animationDelay: `${d}ms` }} />)}
    </div>
  );
}

export function HelpPanel({ variant, chat, busy, starters, firstName, onAsk, onReset, onClose, onNavigate, onTour, onGuide, ia }) {
  const listRef = useRef(null);
  const count = chat.messages.length;
  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [count, busy]);
  const frame = variant === "mobile" ? "inset-0 mx-auto max-w-md"
    : "inset-0 md:inset-auto md:bottom-6 md:right-6 md:h-[min(640px,calc(100vh-3rem))] md:w-[400px] md:rounded-2xl md:border md:shadow-2xl";
  return (
    <motion.section role="dialog" aria-label="Assistente de ajuda" initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 16, scale: 0.98 }} transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
      className={cn("fixed z-[45] flex flex-col overflow-hidden bg-card text-foreground", frame)} data-testid="help-panel">
      <HelpHeader canReset={count > 0} onReset={onReset} onClose={onClose} />
      <div ref={listRef} className="flex-1 space-y-4 overflow-y-auto px-4 py-4" data-testid="help-messages">
        {!count && <HelpWelcome firstName={firstName} starters={starters} onAsk={onAsk} />}
        {chat.messages.map((m, i) => <HelpMessage key={m.id} m={m} index={i} last={i === count - 1} onAsk={onAsk} onNavigate={onNavigate} onTour={onTour} onGuide={onGuide} />)}
        {busy && <Typing />}
      </div>
      <div className="flex items-center justify-between gap-2 border-t px-4 pt-2 text-[11px] leading-snug text-muted-foreground">
        <span data-testid="help-aviso-privacidade">{ia
          ? "As respostas usam inteligência artificial de um provedor externo. Não digite senhas, CSC, dados de clientes, funcionários ou valores."
          : "Respostas do manual do FSC. Não digite senhas, CSC ou dados de clientes."}</span>
        <button type="button" onClick={() => onNavigate("/app/ajuda")} className="shrink-0 font-medium text-primary hover:underline" data-testid="help-manual-link">Ver manual</button>
      </div>
      <HelpComposer disabled={busy} onSend={onAsk} />
    </motion.section>
  );
}
